import { inverseMarketTicker as inverseMarketTickerUtil } from 'constants/forecasts';
import { complementCents } from 'models/market';

/**
 * Plain (non-Decimal) inputs to the canonical price-display logic. Both `buildEventPricingInfo`
 * (the in-app market-entity fields) and the OG share-card route project to these types so they
 * share one source of truth — when the canonical formula changes, both places pick it up.
 */
export interface MarketPricingInfo {
    ticker: string;
    /** Yes-side last price in cents (0-100). For best precision, derive from `last_price_dollars × 100`. */
    lastPriceCents: number;
    /** Contracts traded; `0` invalidates the inverse-via-util path. */
    volume: number;
}

export interface EventPricingInfo {
    markets: MarketPricingInfo[];
    /** Event mutual-exclusivity. This only means at most one market resolves Yes, not that prices sum to 100. */
    mutuallyExclusive: boolean;
    /** True when the event should be treated as a sports game — canonical uses `series.category === 'Sports'`. */
    isSportsCategory: boolean;
}

// General-election race series used by the midterms maps, including legacy House
// tickers and candidate-based California races. Keep this synchronous so server
// renders, live quotes, and share-card charts all resolve the same inverse.
const MIDTERMS_RACE_SERIES_PATTERN =
    /^(?:KXHOUSERACE|KXCAELECTION|(?:KX)?HOUSE[A-Z]{2}(?:\d{1,2}|AL)|(?:KX)?SENATE[A-Z]{2}S?|(?:KX)?GOVPARTY[A-Z]{2})$/;

/**
 * Resolves the "inverse" market whose Yes-price determines this market's displayed value.
 * Events with more than two markets are not price-exhaustive, so their displayed values
 * must use each market's own price instead of assuming an inverse ticker sums to 100.
 */
export function resolveInverseTicker(ownTicker: string, event: EventPricingInfo): string | null {
    if (event.markets.length > 2) return null;

    const utilInverse = inverseMarketTickerUtil(ownTicker);
    if (!utilInverse) {
        const ownIndex = event.markets.findIndex((m) => m.ticker === ownTicker);
        // Existing election mappings (e.g. Senate R -> D) already choose which
        // side to invert. Do not also invert D -> R via the first-market fallback.
        const isUnmappedMidtermsRace =
            MIDTERMS_RACE_SERIES_PATTERN.test(ownTicker.split('-')[0]) &&
            !event.markets.some((market) => inverseMarketTickerUtil(market.ticker));
        const contestBetweenTwo =
            (event.isSportsCategory || isUnmappedMidtermsRace) && event.mutuallyExclusive && event.markets.length === 2;

        if (
            contestBetweenTwo &&
            ownIndex === 0 &&
            event.markets[1].lastPriceCents > 0 &&
            event.markets[1].lastPriceCents < 100
        ) {
            return event.markets[1].ticker;
        }

        return null;
    }

    // Util returned a ticker — verify the inverse market actually traded.
    const inverseMarket = event.markets.find((m) => m.ticker === utilInverse);
    if (!inverseMarket || inverseMarket.volume === 0) return null;
    return utilInverse;
}

/**
 * Yes-perspective last price in cents, using each market's own price for events with more than two markets.
 */
export function normalizedLastYesPriceCents(ownTicker: string, event: EventPricingInfo): number {
    const inv = resolveInverseTicker(ownTicker, event);
    if (inv) {
        const inverseMarket = event.markets.find((m) => m.ticker === inv);
        return complementCents(inverseMarket?.lastPriceCents ?? 0);
    }
    return event.markets.find((m) => m.ticker === ownTicker)?.lastPriceCents ?? 0;
}

/**
 * Decides which market's forecast_history powers a given market's chart line. Mirrors the
 * detail-page chart (`EventPriceHistoriesGraph/GraphContent.tsx`): when the market has an
 * inverse and we have history for that inverse, the line is sourced from the inverse and
 * each point's value is `100 - rawValue`. Otherwise the line uses own history unchanged.
 *
 * Pass `hasHistoryForTicker` so the caller can short-circuit when only one side actually
 * has trading data (the canonical falls back to own history in that case).
 */
export interface ChartLineSource {
    /** Ticker whose forecast_history should be queried/used to draw this market's line. */
    sourceTicker: string;
    /** True when each raw value must be inverted (`100 - rawValue`) to produce the displayed value. */
    invert: boolean;
}

export function resolveChartLineSource(
    ownTicker: string,
    event: EventPricingInfo,
    hasHistoryForTicker: (ticker: string) => boolean,
): ChartLineSource {
    const inverseTicker = resolveInverseTicker(ownTicker, event);
    if (inverseTicker && hasHistoryForTicker(inverseTicker)) {
        return { sourceTicker: inverseTicker, invert: true };
    }
    return { sourceTicker: ownTicker, invert: false };
}
